import React from 'react';
import PropTypes from 'prop-types';
import { List } from 'Components/molecules/List';
import { HtmlSafe } from 'polaris-coreweb/exports';
import { withPolaris } from 'Components/protons/Polaris';
import './_style.scss';

/**
 * BulletListBlock component renders a list of items with bullet points.
 *
 * @component
 * @example
 * const data = [
 *   { title: 'Item 1', content: 'Content 1' },
 *   { title: 'Item 2', content: 'Content 2' }
 * ];
 * return (
 *   <BulletListBlock title="Example Title" data={data} />
 * );
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.context - Context object containing configuration.
 * @param {string} props.context.cssPrefix - CSS prefix for styling.
 * @param {string} props.title - Title of the bullet list.
 * @param {Array} props.data - Array of data objects for list items.
 * @param {string} props.data[].title - Title of the list item.
 * @param {string} props.data[].content - Content of the list item.
 * @returns {React.Element|null} The rendered component or null if no data is provided.
 */
export const BulletListBlock = ({ context: { cssPrefix }, title, data }) => {
  if (!data) return null;

  const content = data.map((item, index) => ({
    title: <HtmlSafe key={index} html={item.content} />,
  }));

  return (
    <div className={`${cssPrefix}__bullet-list-block`}>
      <List
        extraClassNames={{ [`${cssPrefix}__bullet-list-block--list`]: true }}
        title={title}
        content={content}
      />
    </div>
  );
};

BulletListBlock.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  title: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string,
    }),
  ),
};

BulletListBlock.defaultProps = {
  context: { cssPrefix: 'polaris' },
  title: '',
  data: [],
};

export default withPolaris(BulletListBlock);
